import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import DropdownIcon from 'assets/icons/arrow-down-grey.svg?react';

import styles from './DocumentListItem.module.scss';
import { Links } from 'application/constants/links';
import { AppMenuItem } from 'components/uikit/menu/AppMenu';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';
import { OperationType, ProjectResourceType } from 'services/api/api-client';
import { QueryFactory } from 'services/api';
import {
  createEditableDocuments,
  createReadOnlyDocuments,
  selectFiles,
} from 'helpers/documents/fileHandler.ts';
import { createDocumentFolderNode } from 'helpers/documents/documentsObserver.ts';
import { getAllDescendants } from 'helpers/documents/types/base.ts';
import { isDocument } from 'helpers/documents/types/documents.ts';
import { useGridTemplate } from 'helpers/useGridTemplate.ts';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import { useHasProjectPermission } from 'helpers/projectPermissions/useHasProjectPermission.ts';
import { useVariablesContext } from 'pages/authorized/variables/variablesContext/VariablesContext.tsx';

import { AddFolderModal } from '../addFolder/AddFolderModal';

import { TreeItemShell } from './shared/TreeItemShell.tsx';
import { TreeItemHandle } from './shared/TreeItemHandle.tsx';
import { TreeItemName } from './shared/TreeItemName.tsx';
import { TreeItemColumn } from './shared/TreeItemColumn.tsx';
import { TreeItemStatusColumn } from './shared/TreeItemStatusColumn.tsx';
import { TreeItemDotMenu } from './shared/TreeItemDotMenu.tsx';
import { TreeItemDeleteModal } from './shared/TreeItemDeleteModal.tsx';
import { TreeItemEditModal } from './shared/TreeItemEditModal.tsx';
import { useContextualMenu } from './shared/useContextualMenu.ts';
import { useTreeItemMenuItems } from './shared/useTreeItemMenuItems.ts';
import { FolderRowProps } from './shared/types.ts';

export const FolderListItem = forwardRef<HTMLDivElement, FolderRowProps>(
  (props, ref) => {
    const { collapsed, item, parent } = props;

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const i18n = useScopedTranslation(
      'Page.Projects.ProjectDetailsPage.FoldersTree.TreeItem',
    );
    const deleteModalTitle = useScopedTranslation('Component.DeleteItemModal');

    const navigate = useNavigate();
    const params = Links.Authorized.ProjectDetails.useParams();
    const queryClient = useQueryClient();
    const contextualMenu = useContextualMenu();
    const { createDeleteMenuItem, createEditMenuItem } = useTreeItemMenuItems();
    const { refreshVariables } = useVariablesContext();

    const statusAnchorRef = useRef<HTMLElement>(null);

    const hasChildren = item.children && item.children.length > 0;
    const deleteDisabled = !item.canBeDeleted;

    const [folderCollapsed, setFolderCollapsed] = useState(
      hasChildren ? collapsed : true,
    );
    const expanded = !folderCollapsed;

    const hasPermission = useHasProjectPermission();
    const hasAllDocumentPermissions = hasPermission([
      permission(
        ProjectResourceType.Document,
        OperationType.Create,
        OperationType.Read,
        OperationType.Update,
        OperationType.Delete,
      ),
    ]);
    const hasUpdateDocumentPermissions = hasPermission([
      permission(ProjectResourceType.Document, OperationType.Update),
    ]);
    const hasDeleteDocumentPermissions = hasPermission([
      permission(ProjectResourceType.Document, OperationType.Delete),
    ]);

    const onSubfolderDocumentsCreated = async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryFactory.RevisionQuery.getQueryKey(item.revisionId),
        }),
        queryClient.invalidateQueries({
          queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
        }),
        refreshVariables(),
      ]);
      setIsLoading(false);
    };

    const createSubfolderReadonlyDocuments = async () => {
      const files = await selectFiles(false, true);
      contextualMenu.hide();
      setIsLoading(true);

      await createReadOnlyDocuments(
        files,
        item.revisionId,
        undefined,
        onSubfolderDocumentsCreated,
        item.id,
      );
    };

    const createSubfolderEditableDocuments = async () => {
      const files = await selectFiles(true, true);
      contextualMenu.hide();
      setIsLoading(true);

      await createEditableDocuments(
        files,
        item.revisionId,
        undefined,
        onSubfolderDocumentsCreated,
        item.id,
      );
    };

    const onItemDelete = async () => {
      const documentsToDelete = getAllDescendants(item)
        .filter(isDocument)
        .map((doc) => doc.documentId);

      try {
        await QueryFactory.DocumentClient.bulkDelete(documentsToDelete);
        await item.onDelete?.(item.id);
      } catch (e) {
        showErrorToast(e);
        return;
      }
    };

    const onFolderEdit = async (editedName: string) => {
      await item.onFolderEdit(item.id, editedName, 'name');
    };

    const menuItems = useMemo(() => {
      const items: AppMenuItem[] = [
        {
          key: 'create-section',
          text: i18n.t('create_section'),
          className: styles.paleMenuItem,
          disabled: true,
          onClick: () => {},
        },
        {
          key: 'doc',
          text: i18n.t('new_document'),
          className: styles.menuItem,
          onClick: () => {
            navigate(
              Links.Authorized.CreateDocument.link(
                {
                  projectId: item.projectId,
                  releaseId: params.queryParams.releaseId!,
                },
                {
                  folderId: item.id,
                },
              ),
            );
          },
        },
        {
          key: 'folder',
          text: i18n.t('new_folder'),
          className: styles.separatorMenuItem,
          onClick: async () => {
            setIsAddModalOpen(true);
          },
        },
        {
          key: 'import-section',
          text: i18n.t('import_section'),
          className: styles.paleMenuItem,
          disabled: true,
          onClick: () => {},
        },
        {
          key: 'editable-doc',
          text: i18n.t('add_editable'),
          className: styles.menuItem,
          onClick: async () => await createSubfolderEditableDocuments(),
        },
        {
          key: 'read-only-doc',
          text: i18n.t('add_read_only'),
          className: styles.separatorMenuItem,
          onClick: async () => await createSubfolderReadonlyDocuments(),
        },
        createEditMenuItem({
          lockReason: null,
          hidden: !hasUpdateDocumentPermissions,
          onClick: () => {
            contextualMenu.hide();
            setIsEditModalOpen(true);
          },
        }),
        createDeleteMenuItem({
          disabled: deleteDisabled,
          deletionBlockReason: item.deletionBlockReason,
          hidden: !hasDeleteDocumentPermissions,
          onClick: () => {
            contextualMenu.hide();
            setIsDeleteModalOpen(true);
          },
        }),
      ];

      return items;
    }, [
      i18n,
      item.id,
      item.projectId,
      item.revisionId,
      item.deletionBlockReason,
      params.queryParams.releaseId,
      deleteDisabled,
      hasUpdateDocumentPermissions,
      hasDeleteDocumentPermissions,
    ]);

    useEffect(() => {
      setFolderCollapsed(hasChildren ? collapsed : true);
    }, [collapsed, hasChildren]);

    const { grid, hiddenColumns } = useGridTemplate(
      item.projectId,
      'DocumentListItem',
    );

    return (
      <TreeItemShell
        ref={ref}
        treeProps={props}
        isLoading={isLoading}
        onClick={() => setFolderCollapsed(!folderCollapsed)}
        modals={
          <>
            <AddFolderModal
              isOpen={isAddModalOpen}
              onClose={() => setIsAddModalOpen(false)}
              createFolderNode={async (name, id) => {
                const folderNode = createDocumentFolderNode(
                  id,
                  name,
                  params.queryParams.releaseId!,
                  item.projectId,
                );
                setIsAddModalOpen(true);
                await item.onAddSubItem(item.id, folderNode);
                contextualMenu.hide();
              }}
            />
            <TreeItemDeleteModal
              isOpen={isDeleteModalOpen}
              onClose={() => setIsDeleteModalOpen(false)}
              title={deleteModalTitle.t('title_folder')}
              item={item}
              projectId={item.projectId}
              revisionId={item.revisionId}
              onDelete={onItemDelete}
            />
            <TreeItemEditModal
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              item={item}
              parent={parent}
              onSubmit={(name) => onFolderEdit(name)}
            />
          </>
        }
      >
        <TreeItemHandle
          itemId={item.id}
          disableSorting={item.disableSorting}
          handleProps={props.handleProps}
          expanded={expanded}
        >
          <DropdownIcon
            className={
              expanded
                ? styles.folderArrowExpanded
                : styles.folderArrowCollapsed
            }
          />
        </TreeItemHandle>

        <div className={clsx(styles.wrapper, styles.folderWrapper)}>
          <div
            className={
              folderCollapsed ? styles.treeItem : styles.treeItemOpened
            }
            style={{ gridTemplateColumns: grid }}
          >
            <TreeItemName
              name={item.name}
              auditData={null}
              showTooltip={hasAllDocumentPermissions}
            >
              <div data-test-id="list-item-name" className={styles.folderName}>
                {item.name}
              </div>
            </TreeItemName>

            <TreeItemStatusColumn
              hiddenColumns={hiddenColumns}
              itemType={item.type}
              status={item.folderStatus}
              disabled={!hasUpdateDocumentPermissions}
              statusAnchorRef={statusAnchorRef}
            />

            <TreeItemColumn
              column="version"
              hiddenColumns={hiddenColumns}
              className={styles.treeItemVersion}
              testId="version"
            />

            <TreeItemColumn column="sign" hiddenColumns={hiddenColumns} />
          </div>

          <TreeItemDotMenu
            permissions={[
              permission(ProjectResourceType.Revision, OperationType.Update),
            ]}
            menuItems={menuItems}
            anchorRef={contextualMenu.anchorRef}
            isOpen={contextualMenu.isOpen}
            onShow={contextualMenu.show}
            onClose={contextualMenu.hide}
          />
        </div>
      </TreeItemShell>
    );
  },
);
